import { NavigateFunction, useLocation, useNavigate } from "react-router-dom";
import SideNavItem from "./Nav/SideNavItem";
import { sidebarItem } from "../interfaces";
import BaseText from "./BaseText";
import styles from "../styles/Dashboard.module.css";
import BaseButton from "./BaseButton";
import { MouseEvent, useEffect, useState } from "react";
import SvgComponent from "./SVGShape";
import { useAppDispatch } from "../hooks/storeHook";
import { updateCompanyTokenStatus, updateTokenStatus, updateUserProfile } from "../store/reducers/mainReducer";
import { googleLogout } from "@react-oauth/google";
import { createPortal } from "react-dom";
import BaseModal from "./BaseModal";
import useProfileData from "../hooks/UserProfileData";
import NetworkInstance1 from "../utils/alternate";
import UseDynamicLogo from "../hooks/UseDynamicLogo";
import useInitialRender from "../hooks/UseInitialRender";

interface SidebarProps {
  isOpen: boolean;
  hideSidebar: () => void
}

const sidebarItems: sidebarItem[] = [
    {
      title: "Dashboard",
      icon: "dashboard-icon",
      path: "/dashboard",
   
    },
    {
      title: "Rapid Solve",
      icon: "puzzle",
      path: "/rapidsolve",
    },
    {
      title: "Assessments",
      icon: "open-book",
      path: "/assessment",
    },
    {
      title: "Challenges",
      icon: "challenges-icon",
      path: "/challenges",
    },
    // {
    //   title: "Achievements",
    //   icon: "trophy",
    //   path: "/achievements",
    // },
    {
      title: "Community",
      icon: "globe",
      path: "",
    },
    // {
    //   title: "DP generator",
    //   icon: "image_picture",
    //   path: "/dpgenerator",
    // },
    // {
    //   title: "Teams",
    //   icon: "people",
    //   path: "/teams/all",
    // },
    // {
    //   title: "Messages",
    //   icon: "envelope",
    //   path: "/messages",
    // },
  ];

const Sidebar = ({ isOpen, hideSidebar }: SidebarProps) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { showTourGuide }  = useProfileData();
  const navigate: NavigateFunction = useNavigate();
  const networkInstance = NetworkInstance1();


  const currentPath = location.pathname;

  const [tourIntroActive, setTourIntroActive] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [topDistance, setTopDistance] = useState({});
  const [activeTourStep, setActiveTourStep] = useState<number>(0); // starts from 0
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [tourHeader, setTourHeader] = useState<string>("");
  const [tourContext, setTourContext] = useState("");
  const [allSidebarItems, ] = useState<sidebarItem[]>(sidebarItems);
  
  const logo = UseDynamicLogo();

  const sidebarWidth = isOpen ? "w-56" : "hidden"; // Adjust the width as needed
  const tourSteps = [
    "dashboard-icon",
    "open-book",
    "challenges-icon",
    // "trophy",
    "gear",
    "logout-icon",
  ];

  function handleTourNavigationForward(
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) {
    e.preventDefault();
    if (activeTourStep === 4) {
      cancelTour(e);
    }
    setActiveTourStep((prevStep) => Math.min(prevStep + 1, 4));
  }

  const handleTourNavigationBackward = (
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    e.preventDefault();
    setActiveTourStep((prevStep) => Math.max(prevStep - 1, 0));
  };

 

  useEffect(() => {
    computedTopDistance();
    setTourGuideText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTourStep]);

  useEffect(() => {
    if (window.innerWidth > 1024) {
      setTourIntroActive(showTourGuide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTourGuide]);

  const updateProfile = async () => {
    try {
      const payload = {
        showTourGuide: false
      };
      const { status } = await networkInstance.patch('/users/update-profile', payload);
      if (status === 200) {
        dispatch(updateUserProfile({
          showTourGuide: false
        }))
      }
    } catch (err: any) {
    }
  }

  const setTourGuideText = () => {
    switch (activeTourStep) {
      case 0:
        setTourContext("Stay up-to-date with insights on your latest activities, upcoming tasks and important notifications.");
        setTourHeader("Dashboard");
        break;
      case 1:
        setTourContext(
          "Guage your skill level and get a chance to collaborate on challenges with other users on the same level."
        );
        setTourHeader("Assessment");
        break;
      case 2:
        setTourContext(
          "Join a challenge to gain valuable experience, build an impressive portfolio and earn points and badges on the way."
        );
        setTourHeader("Challenges");
        break;
      // case 3:
      //   setTourContext("View your achievements, track your progress and share your milestones with others.");
      //   setTourHeader("Achievements");
      //   break;
      // case 4:
      //   setTourContext(
      //     "Check out prospective team members before you join a challenge."
      //   );
      //   setTourHeader("Teams");
      //   break;
      // case 5:
      //   setTourContext("Chat in real-time with team members and grow your professional network");
      //   setTourHeader("Messages");
      //   break;
      case 3:
        setTourContext("Customise your account to your preferred settings");
        setTourHeader("Settings");
        break;
      case 4:
        setTourContext("Log out from your account.");
        setTourHeader("Log out");
        break;
    }
  };

  const cancelTour = (
    e:
      | MouseEvent<HTMLDivElement, globalThis.MouseEvent>
      | MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    e.preventDefault();
    setIsTourActive(false);
    setTopDistance({});
    setActiveTourStep(0);
    updateProfile();
  };

  const activeClassName = (path: string) =>
    currentPath.startsWith(`/${path.split('/')[1]}`) ? "active" : "";


  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 1024);
    };

    handleResize()

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isLargeScreen]);

  useInitialRender(() => {
    if (activeTourStep !== 5) {
      const buttonId = `button-${tourSteps[activeTourStep]}`;
      const anchorElement = document.getElementById(buttonId);

      if (!anchorElement) {
        // Early return if anchor element is not found
        return;
      }
      const anchorRect = anchorElement.getBoundingClientRect();
      setTopDistance({
        top: anchorRect.top - (anchorRect.height + 20),
        transition: 'top 0.5s ease-out',
      });
    }
    setTourGuideText();
    
  });



  const computedTopDistance = () => {
    if (activeTourStep !== 4) {
      const buttonId = `button-${tourSteps[activeTourStep]}`;
      const anchorElement = document.getElementById(buttonId);
      if (!anchorElement) {
        // Early return if anchor element is not found
        return;
      }
      const anchorRect = anchorElement.getBoundingClientRect();
      setTopDistance({
        top: anchorRect.top - (anchorRect.height + 20),
        transition: 'top 0.5s ease-out',
      });
    }
  };

  const handleTourStart = () => {
    setTourIntroActive(false);
    setIsTourActive(true);
  };

  const handleTourCancel = () => {
    setTourIntroActive(false);
    updateProfile();
  };

  const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(updateTokenStatus(null));
    dispatch(updateCompanyTokenStatus(null));
    if (!isLargeScreen) {
      hideSidebar()
    }
    navigate("/auth/signup");
    try {
      googleLogout();
    } catch (err) {
      console.log(err);
    }
  };

  const computeActiveState = (item: sidebarItem) =>
    isTourActive
      ? tourSteps[activeTourStep] === `${item.icon}`
      : Boolean(activeClassName(item.path));

  return (
    <nav
      id="side-navigation"
      className={`bg-white z-10 h-screen border-r-2 border-gray-100 pt-20 pb-10 fixed flex flex-col items-start justify-between no-scrollbar overflow-y-auto ease-in-out transition-all duration-300 ${sidebarWidth}`}
    >
       <div onClick={() => {
        if (currentPath !== "/dashboard") {
          navigate("/dashboard");
        }
       }} className="absolute z-10 top-5 left-9">
      <img width={100} height="auto" src={logo} alt="Talenvo App" />
      </div>
      <div className="pl-2">

        {allSidebarItems.map((item) => (
          <SideNavItem
            buttonId={item.icon}
            key={item.title}
            title={item.title}
            icon={item.icon}
            path = {item.path}
            isActive={computeActiveState(item)}
            handleBtnClick={(e) => {
              if (!isLargeScreen) {
                hideSidebar()
              }
              // if (item.path) {
              //   navigate(item.path);
              // }
              if (item.title === "Community"){
                setShowCommunityModal(true)
              }
            }}
          />
        ))}

        {/* COMMUNITY MODAL */}
        <BaseModal isOpen={showCommunityModal} onClose={()  => setShowCommunityModal(false)} boxWidth="max-w-lg" >
            <div className="flex flex-col text-center px-8 justify-center items-center gap-4">
               <div className="bg-[#04329914] w-[100px] h-[100px] rounded-md flex justify-center items-center">
              <SvgComponent icon="telegram" />
            </div>
              <BaseText size="x-large" weight="bold" >
              Join our WhatsApp community
              </BaseText>
              <BaseText size="small" weight="normal" >
               Get notified on the latest projects <br/> and hackathons
              </BaseText>
              <div className="flex gap-4 justify-center items-center p-3" >
                <BaseButton   handleEngagement={() => window.open("https://d7b75.r.a.d.sendibm1.com/mk/cl/f/sh/1f8JIKXwHGYox3HcP7qZASLvBH/KlcVc0YpewKs", "_blank")}>
                  <BaseText weight="semibold" extraClasses="px-6 text-xs sm:text-sm ">
                  Join
                  </BaseText>
                </BaseButton>
      
              </div>
            </div>
          </BaseModal>
      </div>

      {/* TOUR GUIDE INTRO */}
      {tourIntroActive && (
        <BaseModal
          padded={false}
          isOpen={tourIntroActive}
          onClose={() => setTourIntroActive(false)}
          boxWidth={"max-w-xl"}
        >
          <div className="flex flex-col text-center justify-center items-center gap-4 m-0 transition ease-in delay-1000">
            <div className="bg-[#043299] rounded-l w-full flex py-7 justify-center">
              <SvgComponent icon="confetti" />
            </div>
            <div className="gap-8 flex pb-8 flex-col">
              <BaseText
                size="x-large"
                color="black"
                weight="bold"
                extraClasses="transition ease-in pt-6 delay-150 "
              >
               Welcome to Talenvo!
              </BaseText>
              <BaseText
                size="smaller"
                color="gray"
                extraClasses="transition ease-in delay-50 px-16"
              >
                Ready to begin your journey? 
                <br/>
                <span className=""/>
                Our guided tour helps you master each feature and navigate Talenvo like a pro.
                <span/>
                <br/>
                Dive in now or save it for later.
              </BaseText>
              <div className="flex justify-center gap-16 w-full items-center">
                <button
                  className="outline-0 border-0 bg-none "
                  onClick={handleTourCancel}
                >
                  <BaseText weight="medium" size="small" color="blue">
                    No, Skip
                  </BaseText>
                </button>
                <BaseButton handleEngagement={handleTourStart}>
                  <BaseText weight="semibold" size="small">
                    Yes, take tour
                  </BaseText>
                </BaseButton>
              </div>
            </div>
          </div>
        </BaseModal>
      )}
      {/* TOUR GUIDE INTRO */}

      {/* TOUR GUIDE STARTS */}
      {isTourActive &&
        createPortal(
          <div
          className='flex items-center z-50 absolute left-56 '
          style={topDistance}
        >
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div
            className={` ${activeTourStep === 4 ? styles.tourLast : ""} ${
              styles.tour
            } flex flex-col justify-evenly w-80 py-10 px-7 h-56 relative z-50`}
          >
              <div className="flex justify-between items-center mb-4">
                <BaseText
                  weight="semibold"
                  size="small"
                  color="black"
                  extraClasses="transition ease-in delay-50 "
                >
                  {tourHeader}
                </BaseText>
                <div onClick={(e) => cancelTour(e)}>
                  <BaseText weight="medium" size="smaller" color="blue">
                    Skip
                  </BaseText>
                </div>
              </div>
              <BaseText
                extraClasses="my-5 transition ease-out delay-150 slide-fade-in slide-out"
                weight="normal"
                size="smaller"
                color="black"
              >
                {tourContext}
              </BaseText>
              <div className="flex justify-end outline-0 border-0 gap-7 w-full items-center">
             { activeTourStep !== 0 &&  <button
                  className="outline-0 border-0 bg-none "
                  onClick={handleTourNavigationBackward}
                >
                  <BaseText weight="medium" size="xs-small" color="black">
                    Back
                  </BaseText>
                </button>}

                <button
                  onClick={handleTourNavigationForward}
                  className="outline-0 border-0 bg-[#EAECF0] px-4 py-2 rounded-xl cursor-pointer "
                >
                  <BaseText weight="medium" size="xs-small" color="blue">
                    {activeTourStep === 4 ? "Finish" : "Next"}
                  </BaseText>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <div className="py-15 border-t-2 border-gray-200 w-full flex flex-col items-start justify-end gap-2">
        <div className="pl-3 mt-4">
          <SideNavItem
            buttonId={"gear"}
            title={"Settings"}
            icon={"gear"}
            isActive={computeActiveState({
              title: "Settings",
              icon: "gear",
              path: "/settings",
            })}
            handleBtnClick={(e) => {
              if (!isLargeScreen) {
                hideSidebar()
              }
              navigate("/settings");
            }}
          />
        </div>

        <div className="pl-9 flex w-full ">
        <BaseButton
          buttonId={"logout-icon"}
          size="small"
          handleEngagement={(e) => handleLogout(e)}
        >
          <div className="flex items-center gap-4 justify-center">
            <span>
              <SvgComponent icon="logout-icon" />
            </span>
            <BaseText weight="semibold" size="small">
              Logout
            </BaseText>
          </div>
        </BaseButton>
          </div>

      </div>
    </nav>
  );
};

export default Sidebar;
